// Time is stored in UTC and displayed in East Africa Time (CLAUDE.md, domain rule 5).
//
// EAT is UTC+3 all year: Kenya has never observed daylight saving and has had no offset
// change since 1960. That is why this module does arithmetic on a fixed offset instead of
// going through the IANA database — a fixed offset is exact, has no locale or ICU build to
// depend on, and cannot shift under a Node upgrade. If Wodi is ever deployed outside East
// Africa this becomes a per-facility timezone and this module has to be revisited.
//
// Nothing here reads the clock. Anything that needs "now" takes the instant as an argument,
// so a daily report, a shift boundary and an age are all reproducible in a test.

import type { Result } from './result.ts';
import { err, ok } from './result.ts';

/** East Africa Time is UTC+3, with no daylight saving. */
export const EAT_UTC_OFFSET_MINUTES = 180;

const MILLIS_PER_MINUTE = 60_000;
const MILLIS_PER_DAY = 86_400_000;
const EAT_OFFSET_MILLIS = EAT_UTC_OFFSET_MINUTES * MILLIS_PER_MINUTE;

/**
 * A date on the calendar with no time and no timezone: a date of birth, a report day, the
 * day a shift belongs to. Months are 1–12 and days are 1–31, as people write them, not as
 * `Date` numbers them.
 */
export interface CalendarDay {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** A wall-clock time in EAT. */
export interface ClockTime {
  readonly hour: number;
  readonly minute: number;
}

export type TimeErrorCode = 'time.empty' | 'time.malformed_date' | 'time.impossible_date';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const TIME_ERRORS: Record<TimeErrorCode, string> = {
  'time.empty': 'No date was entered',
  'time.malformed_date': 'A date is written as YYYY-MM-DD',
  'time.impossible_date': 'That date does not exist',
};

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** `Jan` to `Dec`, as dates are written (DESIGN.md §9). */
export const MONTH_ABBREVIATIONS: readonly string[] = MONTH_NAMES;

const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

// --- Calendar days -----------------------------------------------------------------

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  const days = DAYS_PER_MONTH[month - 1];
  if (days === undefined) throw new RangeError(`Month out of range: ${month}`);
  return days;
}

export function isValidDay(day: CalendarDay): boolean {
  if (!Number.isInteger(day.year) || !Number.isInteger(day.month) || !Number.isInteger(day.day))
    return false;
  if (day.month < 1 || day.month > 12) return false;
  return day.day >= 1 && day.day <= daysInMonth(day.year, day.month);
}

function assertValidDay(day: CalendarDay): void {
  if (!isValidDay(day))
    throw new RangeError(`Not a date on the calendar: ${day.year}-${day.month}-${day.day}`);
}

/**
 * Milliseconds at midnight UTC on that calendar day. Built with `setUTCFullYear` rather
 * than `Date.UTC`, which maps two-digit years into the 1900s.
 */
function utcMidnightMillis(day: CalendarDay): number {
  const date = new Date(0);
  date.setUTCFullYear(day.year, day.month - 1, day.day);
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
}

function dayFromUtcMillis(millis: number): CalendarDay {
  const date = new Date(millis);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

/** Negative when a is earlier, zero when equal, positive when a is later. */
export function compareDays(a: CalendarDay, b: CalendarDay): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

export function daysEqual(a: CalendarDay, b: CalendarDay): boolean {
  return compareDays(a, b) === 0;
}

/** Whole days from one calendar day to another; negative when `to` is earlier. */
export function daysBetween(from: CalendarDay, to: CalendarDay): number {
  assertValidDay(from);
  assertValidDay(to);
  return Math.round((utcMidnightMillis(to) - utcMidnightMillis(from)) / MILLIS_PER_DAY);
}

export function addDays(day: CalendarDay, count: number): CalendarDay {
  assertValidDay(day);
  if (!Number.isSafeInteger(count)) throw new RangeError('Day count must be a whole number');
  return dayFromUtcMillis(utcMidnightMillis(day) + count * MILLIS_PER_DAY);
}

// --- Instants and EAT ---------------------------------------------------------------

function assertValidInstant(instant: Date): void {
  if (Number.isNaN(instant.getTime())) throw new RangeError('Not a valid instant');
}

/**
 * Shifts an instant by the EAT offset so the UTC getters read the East African wall clock.
 * The shifted `Date` is not a real instant and never leaves this module.
 */
function eatWallClock(instant: Date): Date {
  assertValidInstant(instant);
  return new Date(instant.getTime() + EAT_OFFSET_MILLIS);
}

/**
 * The EAT calendar day an instant falls on. This is the day a visit, a payment or a
 * dispense belongs to for a daily report or a cashier shift: 21:30 UTC on 21 Sep is
 * already 22 Sep in Nairobi.
 */
export function eatDayOf(instant: Date): CalendarDay {
  const wall = eatWallClock(instant);
  return {
    year: wall.getUTCFullYear(),
    month: wall.getUTCMonth() + 1,
    day: wall.getUTCDate(),
  };
}

export function eatTimeOf(instant: Date): ClockTime {
  const wall = eatWallClock(instant);
  return { hour: wall.getUTCHours(), minute: wall.getUTCMinutes() };
}

/** The instant EAT midnight falls at, in UTC. */
export function startOfEatDay(day: CalendarDay): Date {
  assertValidDay(day);
  return new Date(utcMidnightMillis(day) - EAT_OFFSET_MILLIS);
}

export interface InstantRange {
  readonly startInclusive: Date;
  readonly endExclusive: Date;
}

/**
 * The UTC instants that bound one EAT calendar day, half-open so consecutive days neither
 * overlap nor leave a gap. This is the range a daily report or a shift query uses:
 * `created_at >= startInclusive AND created_at < endExclusive`.
 */
export function eatDayRange(day: CalendarDay): InstantRange {
  return { startInclusive: startOfEatDay(day), endExclusive: startOfEatDay(addDays(day, 1)) };
}

/**
 * The UTC instants that bound one EAT calendar month (months 1–12), half-open: from EAT
 * midnight on the 1st to EAT midnight on the 1st of the next month. Monthly returns and
 * month filters use this, never a UTC month.
 */
export function eatMonthRange(year: number, month: number): InstantRange {
  const first = { year, month, day: 1 };
  assertValidDay(first);
  const next =
    month === 12 ? { year: year + 1, month: 1, day: 1 } : { year, month: month + 1, day: 1 };
  return { startInclusive: startOfEatDay(first), endExclusive: startOfEatDay(next) };
}

export function isWithinEatDay(instant: Date, day: CalendarDay): boolean {
  assertValidInstant(instant);
  const { startInclusive, endExclusive } = eatDayRange(day);
  const time = instant.getTime();
  return time >= startInclusive.getTime() && time < endExclusive.getTime();
}

// --- Display (DESIGN.md §9) -----------------------------------------------------------

/**
 * `22 Sep 2026` (DESIGN.md §9). The day of the month is padded to two digits, so a column
 * of dates lines up with the tabular figures the tables use.
 */
export function formatDay(day: CalendarDay): string {
  assertValidDay(day);
  const month = MONTH_NAMES[day.month - 1];
  if (month === undefined) throw new RangeError(`Month out of range: ${day.month}`);
  return `${String(day.day).padStart(2, '0')} ${month} ${String(day.year)}`;
}

/** `2026-09-22`, for anything machine-readable: query params, file names, sort keys. */
export function formatIsoDay(day: CalendarDay): string {
  assertValidDay(day);
  return `${String(day.year).padStart(4, '0')}-${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
}

/** `22 Sep 2026`, in East Africa Time. */
export function formatEatDate(instant: Date): string {
  return formatDay(eatDayOf(instant));
}

/** 24-hour `14:05`, in East Africa Time. */
export function formatEatTime(instant: Date): string {
  const { hour, minute } = eatTimeOf(instant);
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * `22 Sep 2026 14:05`, in East Africa Time. DESIGN.md §9 fixes the date and the time but
 * not the join; a single space keeps both readable and keeps the string short enough for a
 * table cell at 1366x768.
 */
export function formatEatDateTime(instant: Date): string {
  return `${formatEatDate(instant)} ${formatEatTime(instant)}`;
}

/** Reads `2026-09-22`. Strict: this is for machine input, not for what a person types. */
export function parseIsoDay(text: string): Result<CalendarDay, TimeErrorCode> {
  const trimmed = text.trim();
  if (trimmed === '') return err('time.empty');
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return err('time.malformed_date');
  const [, year = '', month = '', dayOfMonth = ''] = match;
  const day = { year: Number(year), month: Number(month), day: Number(dayOfMonth) };
  if (day.month < 1 || day.month > 12 || !isValidDay(day)) return err('time.impossible_date');
  return ok(day);
}

// --- Age -------------------------------------------------------------------------------

export type AgeUnit = 'days' | 'months' | 'years';

export interface Age {
  readonly unit: AgeUnit;
  readonly value: number;
}

/**
 * Below this age an age is shown in days. 28 days is the neonatal period, which is what
 * "newborn" means clinically, and it is the boundary a triage nurse already works to.
 *
 * TODO(verify-spec): DESIGN.md §9 says "days for newborns" without fixing the cut-off.
 * Confirm with the clinical lead whether Wodi should switch to months at 28 days or at
 * one calendar month.
 */
export const NEWBORN_DAYS = 28;

/** Below this age an age is shown in months (DESIGN.md §9: "months for under-2s"). */
export const MONTHS_UNTIL_YEARS = 24;

/**
 * Whole months lived, counting an anniversary that falls on a day the month does not have
 * as the last day of that month. Someone born on the 31st has a monthly anniversary on
 * 28 or 29 February; someone born on 29 February has one on 28 February in common years.
 *
 * TODO(verify-spec): where Kenyan law or SHA rules fix the anniversary of a 29 February
 * birth for age-based entitlements, that rule wins over this one. The clamp is the common
 * software convention, not a verified legal position.
 */
export function completedMonths(dateOfBirth: CalendarDay, on: CalendarDay): number {
  const months = (on.year - dateOfBirth.year) * 12 + (on.month - dateOfBirth.month);
  const anniversaryDay = Math.min(dateOfBirth.day, daysInMonth(on.year, on.month));
  return on.day < anniversaryDay ? months - 1 : months;
}

/**
 * Age on a given calendar day: days for a newborn, months under two years, whole years
 * after that (DESIGN.md §9).
 *
 * Throws when the date of birth is after the day asked about. A future date of birth is a
 * data-entry error to be caught when it is entered, not something to show on a banner.
 */
export function ageOnDay(dateOfBirth: CalendarDay, on: CalendarDay): Age {
  const days = daysBetween(dateOfBirth, on);
  if (days < 0) throw new RangeError('Date of birth is after the day asked about');
  if (days < NEWBORN_DAYS) return { unit: 'days', value: days };

  const months = completedMonths(dateOfBirth, on);
  if (months < MONTHS_UNTIL_YEARS) return { unit: 'months', value: months };
  return { unit: 'years', value: Math.floor(months / 12) };
}

/**
 * Age at an instant, read on the East African calendar. The instant is explicit so that a
 * banner, a report and a test all agree: pass the request time, never a clock read here.
 */
export function ageAt(dateOfBirth: CalendarDay, asAt: Date): Age {
  return ageOnDay(dateOfBirth, eatDayOf(asAt));
}

/** `6 days`, `18 months`, `34 years`. */
export function formatAge(age: Age): string {
  const unit = age.value === 1 ? age.unit.slice(0, -1) : age.unit;
  return `${String(age.value)} ${unit}`;
}
